import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of, throwError, forkJoin, from, firstValueFrom } from 'rxjs';
import { catchError, switchMap, takeWhile, map, startWith } from 'rxjs/operators';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { ErrorLoggerService } from '../services/error-logger.service';
import { environment } from '../../environments/environment';

import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  limit as fsLimit,
  Unsubscribe
} from 'firebase/firestore';

import { MediaItem, MediaType, MediaCategory } from '../../typings/media.interface';

export interface UploadProgress {
  status: 'progress' | 'complete' | 'error';
  percentage: number;
  mediaId?: string;
  error?: string;
}

export type AnalysisState = 'processing' | 'ready' | 'failed';

export interface AnalysisProgressState {
  state: AnalysisState;
  progress: number;         // 0-100
  done: number;             // number done (ready+failed)
  total: number;            // total ids
  ready: number;
  failed: number;
}

// Backend job doc (Firestore) shape from resumeenhancer/worker/main.py
type MediaJobStatus =
  | 'UPLOADING'
  | 'UPLOADED'
  | 'QUEUED'
  | 'ANALYZING'
  | 'VIDEO_PROCESSING'
  | 'COMPLETED'
  | 'ERROR';

export interface MediaJobDoc {
  jobId: string;
  uid: string;
  assetId?: string;
  mediaType: 'image' | 'video' | 'unknown';
  bucket?: string;
  object?: string;
  gcsUri?: string;
  contentType?: string;
  status?: MediaJobStatus;
  progressPercent?: number;
  caption?: string;
  description?: string;
  category?: string;
  categories?: string[];
  error?: string;
  createdAt?: string;
  updatedAt?: string;
  metadataGsUri?: string;
  videoIntelligenceOperation?: string;
}

export interface CreateMediaUploadUrlResponse {
  jobId: string;
  assetId: string;
  mediaType: 'image' | 'video' | 'unknown';
  bucket: string;
  object: string;
  gcsUri: string;
  contentType: string;
  uploadUrl: string;
}

export interface AnalyzeMediaResponse {
  jobId: string;
  status: string;
  mediaType: 'image' | 'video' | 'unknown';
  gcsUri: string;
  caption?: string;
  description?: string;
  category?: string;
  categories?: string[];
  videoIntelligenceOperation?: string;
  metadataGsUri?: string;
}

@Injectable({ providedIn: 'root' })
export class MediaLibraryService {
  private readonly itemsSubject = new BehaviorSubject<MediaItem[]>([]);
  readonly items$ = this.itemsSubject.asObservable();

  /**
   * Base URL for routed media endpoints via enhanceResume:
   * /createMediaUploadUrl, /uploadMediaToGcs, /analyzeMedia, /pollMediaJob, /mediaList
   */
  private readonly mediaFunctionsBaseUrl =
    (environment as any).mediaFunctionsBaseUrl ||
    'https://us-central1-ringed-codex-484318-g6.cloudfunctions.net/enhanceResume';

  /**
   * Media endpoints - all routed through enhanceResume Cloud Function
   */
  private readonly createUploadUrlEndpoint =
    (environment as any).mediaUploadUrl || `${this.mediaFunctionsBaseUrl}/createMediaUploadUrl`;
  private readonly uploadMediaToGcsEndpoint =
    (environment as any).mediaUploadToGcsUrl || `${this.mediaFunctionsBaseUrl}/uploadMediaToGcs`;
  private readonly analyzeMediaEndpoint =
    (environment as any).mediaAnalyzeUrl || `${this.mediaFunctionsBaseUrl}/analyzeMedia`;
  private readonly pollMediaJobEndpoint =
    (environment as any).mediaPollJobUrl || `${this.mediaFunctionsBaseUrl}/pollMediaJob`;
  private readonly mediaListUrl =
    (environment as any).mediaListUrl || `${this.mediaFunctionsBaseUrl}/mediaList`;
  // Gemini 3 Flash Preview endpoint (deployed Cloud Function)
  private readonly geminiEndpoint =
    (environment as any).geminiEndpoint || `${this.mediaFunctionsBaseUrl}/generateGemini`;

  private readonly mediaJobsCollectionName =
    (environment as any).mediaJobsCollectionName || 'mediaJobs';

  // Final library collection (populated by backend regardless of job outcome)
  private readonly mediaCollectionName =
    (environment as any).mediaCollectionName || 'media';

  // Session identifier stored on docs (optional). If set, we can filter down to a single session.
  private readonly mediaSessionId = (environment as any).mediaSessionId || 'user-session';

  private firestoreUnsubscribe: Unsubscribe | null = null;
  private db = getFirestore();

  private analysisPollingSubs: Subscription[] = [];
  private readonly backendMediaLoadLimit = 200;

  constructor(
    private http: HttpClient,
    private errorLogger: ErrorLoggerService
  ) {
    // Best-effort: do not throw at app boot if auth/session not ready.
    this.initializeMediaListener().catch(() => {
      this.itemsSubject.next([]);
    });

    onAuthStateChanged(getAuth(), (user) => {
      if (!user?.uid) {
        this.itemsSubject.next([]);
        return;
      }
      this.initializeMediaListener().catch(() => {
        this.itemsSubject.next([]);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // List Media from Backend
  // ---------------------------------------------------------------------------

  /**
   * List media items from backend endpoint (uses Firestore index on server side).
   * This avoids client-side Firestore index requirements.
   */
  listMedia(limit: number = 50, offset: number = 0): Observable<{ items: MediaItem[]; count: number }> {
    return new Observable(observer => {
      this.getCurrentUserInfo().then(async ({ uid }) => {
        try {
          if (!uid) {
            observer.next({ items: [], count: 0 });
            observer.complete();
            return;
          }

          // Require Firebase Auth
          const auth = getAuth();
          if (!auth.currentUser) {
            observer.error(new Error('Authentication required. Please log in.'));
            return;
          }

          const token = await auth.currentUser.getIdToken().catch((err) => {
            observer.error(new Error('Failed to get authentication token.'));
            return null;
          });

          if (!token) {
            return; // Error already handled
          }

          const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
          // Backend extracts uid from auth token, so we only send limit and offset
          const params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString());

          this.http.get<any>(this.mediaListUrl, { headers, params })
            .pipe(
              map((raw) => {
                const response = this.unwrapBackendJson(raw) as { items?: any[]; count?: number };
                const mappedItems = (response.items || [])
                  .map((item) => this.mapMediaToMediaItem(item))
                  .filter((item): item is MediaItem => !!item);
                return {
                  items: mappedItems,
                  count: response.count ?? response.items?.length ?? 0
                };
              }),
              catchError(err => {
                this.errorLogger.logError(err, 'MediaLibrary: List Media Error', uid);
                observer.error(err);
                return throwError(() => err);
              })
            )
            .subscribe({
              next: (result) => {
                observer.next(result);
                observer.complete();
              },
              error: (err) => observer.error(err)
            });
        } catch (err) {
          this.errorLogger.logError(err, 'MediaLibrary: List Media Error', uid || 'unknown');
          observer.error(err);
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Gemini JSON-fence normalization (backend may store raw ```json ... ```)
  // ---------------------------------------------------------------------------

  /**
   * Unwrap nested JSON envelopes from Cloud Functions / proxies (result, data, stringified body, etc.).
   * Fixes false "no jobId" when HTTP 200 returns a wrapped payload.
   */
  private unwrapBackendJson(value: any, depth = 0): any {
    if (value == null || depth > 10) {
      return value;
    }
    if (typeof value === 'string') {
      const t = value.trim();
      if (
        (t.startsWith('{') && t.endsWith('}')) ||
        (t.startsWith('[') && t.endsWith(']'))
      ) {
        try {
          return this.unwrapBackendJson(JSON.parse(t), depth + 1);
        } catch {
          return value;
        }
      }
      return value;
    }
    if (typeof value !== 'object') {
      return value;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'result') && value.result !== undefined) {
      return this.unwrapBackendJson(value.result, depth + 1);
    }
    if (
      Object.prototype.hasOwnProperty.call(value, 'data') &&
      value.data !== undefined &&
      typeof value.data === 'object'
    ) {
      return this.unwrapBackendJson(value.data, depth + 1);
    }
    if (Object.prototype.hasOwnProperty.call(value, 'payload') && value.payload != null) {
      return this.unwrapBackendJson(value.payload, depth + 1);
    }
    if (typeof (value as any).body === 'string' && String((value as any).body).trim()) {
      try {
        return this.unwrapBackendJson(JSON.parse((value as any).body), depth + 1);
      } catch {
        return value;
      }
    }
    if (typeof (value as any).body === 'object' && (value as any).body !== null) {
      return this.unwrapBackendJson((value as any).body, depth + 1);
    }
    return value;
  }

  private extractJobIdFromResponse(raw: any): string | null {
    const p = this.unwrapBackendJson(raw);
    if (!p || typeof p !== 'object') {
      return null;
    }
    const id =
      (p as any).jobId ??
      (p as any).job_id ??
      (p as any).id;
    if (typeof id === 'string' && id.trim()) {
      return id.trim();
    }
    return null;
  }

  private stripJsonFences(text: string): string {
    const t = String(text || '').trim();
    if (!t.startsWith('```')) return t;
    const firstNl = t.indexOf('\n');
    let body = firstNl >= 0 ? t.slice(firstNl + 1) : t;
    body = body.trim();
    if (body.endsWith('```')) body = body.slice(0, -3).trim();
    return body;
  }

  private tryParseGeminiJson(text: string): any | null {
    const cleaned = this.stripJsonFences(text);
    try {
      const parsed = JSON.parse(cleaned);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  async loadAllMediaForUser(limit: number = 200): Promise<void> {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        await new Promise<void>((resolve) => {
          const unsub = onAuthStateChanged(auth, (user) => {
            if (user?.uid || user === null) {
              unsub();
              resolve();
            }
          });
        });
      }

      if (!auth.currentUser) {
        this.itemsSubject.next([]);
        return;
      }

      const uid = auth.currentUser.uid;
      const allItems: MediaItem[] = [];
      let offset = 0;
      while (true) {
        const result = await firstValueFrom(this.listMedia(limit, offset));
        allItems.push(...(result.items || []));
        const batchSize = (result.items || []).length;
        offset += batchSize;
        // Backend returns page count, not total count. Continue until short page.
        if (batchSize < limit) break;
      }

      const firestoreItems = await this.loadAllMediaFromFirestore(uid);
      this.itemsSubject.next(this.filterItemsForUid(this.mergeMediaItems(allItems, firestoreItems), uid));
    } catch (err) {
      this.errorLogger.logError(err, 'MediaLibrary: Load All Media Error', 'unknown');
    }
  }

  private async loadAllMediaFromFirestore(uid: string): Promise<MediaItem[]> {
    if (!uid) return [];
    try {
      const mediaCollection = collection(this.db, this.mediaCollectionName);
      const qRef = query(mediaCollection, where('uid', '==', uid), fsLimit(500));
      const snapshot = await getDocs(qRef);
      const items: MediaItem[] = [];
      snapshot.forEach((docSnap) => {
        const mapped = this.mapMediaToMediaItem({ ...docSnap.data(), id: docSnap.id });
        if (mapped) items.push(mapped);
      });
      return items;
    } catch (err) {
      this.errorLogger.logError(err, 'MediaLibrary: Firestore fallback load', uid);
      return [];
    }
  }

  private mergeMediaItems(primary: MediaItem[], secondary: MediaItem[]): MediaItem[] {
    const byId = new Map<string, MediaItem>();
    [...secondary, ...primary].forEach((item) => {
      if (!item?.id) return;
      byId.set(item.id, item);
    });
    return Array.from(byId.values()).sort((a, b) => {
      const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return tb - ta;
    });
  }

  private filterItemsForUid(items: MediaItem[], uid: string): MediaItem[] {
    if (!uid) return [];
    return (items || []).filter((item) => !!item && item.uid === uid);
  }

  private async loadAllBackendMediaForUid(uid: string, limit: number = this.backendMediaLoadLimit): Promise<MediaItem[]> {
    if (!uid) return [];
    const allItems: MediaItem[] = [];
    let offset = 0;
    while (true) {
      const result = await firstValueFrom(this.listMedia(limit, offset));
      const batch = this.filterItemsForUid(result.items || [], uid);
      allItems.push(...batch);
      const batchSize = batch.length;
      offset += batchSize;
      if (batchSize < limit) break;
    }
    return allItems;
  }

  private normalizeAnalysisFields(data: any): {
    caption: string;
    description: string;
    category: string;
    categories: string[];
  } {
    const rawCaption = String(data?.caption || '').trim();
    const rawDescription = String(data?.description || '').trim();
    const rawCategory = String(data?.category || '').trim();
    const rawCategories = Array.isArray(data?.categories) ? data.categories : [];

    // Backend sometimes returns a JSON blob in caption/description.
    const fromCaption = rawCaption ? this.tryParseGeminiJson(rawCaption) : null;
    const fromDescription = rawDescription ? this.tryParseGeminiJson(rawDescription) : null;
    const j = fromCaption || fromDescription;

    const caption = String(j?.caption || rawCaption || '').trim();
    const description = String(j?.description || rawDescription || '').trim();
    const category = String(j?.category || rawCategory || '').trim();
    const categories = Array.isArray(j?.categories)
      ? (j.categories.filter((x: any) => typeof x === 'string' && x.trim()) as string[])
      : (rawCategories.filter((x: any) => typeof x === 'string' && x.trim()) as string[]);

    return { caption, description, category, categories };
  }

  // ---------------------------------------------------------------------------
  // Real-time Listener (Library stream) - Optional: Keep for realtime updates
  // ---------------------------------------------------------------------------

  private async initializeMediaListener(): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user?.uid) {
      // No realtime updates until logged in
      this.itemsSubject.next([]);
      return;
    }

    const uid = user.uid;
    if (this.firestoreUnsubscribe) this.firestoreUnsubscribe();

    // Try Firestore listener first (realtime updates)
    // If index is not ready, fall back to polling backend endpoint
    try {
      const mediaCollection = collection(this.db, this.mediaCollectionName);
    const q = query(
      mediaCollection,
      where('uid', '==', uid),
      orderBy('createdAt', 'desc')
    );

    this.firestoreUnsubscribe = onSnapshot(q, async (snapshot) => {
      const firestoreItems: MediaItem[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const mapped = this.mapMediaToMediaItem({ ...data, id: docSnap.id });
        if (mapped) firestoreItems.push(mapped);
      });

      // Merge realtime Firestore with backend listing to avoid transient misses.
      let backendItems: MediaItem[] = [];
      try {
        backendItems = await this.loadAllBackendMediaForUid(uid);
      } catch (err) {
        this.errorLogger.logError(err, 'MediaLibrary: backend merge in listener failed', uid);
      }

      const merged = this.filterItemsForUid(this.mergeMediaItems(backendItems, firestoreItems), uid);
      this.itemsSubject.next(merged);
      }, (error: any) => {
        // Check if error is index-related
        const isIndexError = error?.code === 'failed-precondition' || 
                           error?.message?.includes('index') ||
                           error?.message?.includes('The query requires an index');

        if (isIndexError) {
          console.warn('Firestore index not ready, falling back to backend polling:', error.message);
          this.errorLogger.logError(error, 'MediaLibrary: Firestore Index Not Ready - Using Backend Fallback', uid);
          // Fall back to polling backend endpoint
          this.startBackendPolling(uid);
        } else {
      console.error("Firestore listener error:", error);
      this.errorLogger.logError(error, 'MediaLibrary: Firestore Listener', uid);
          // For other errors, still try backend fallback
          this.startBackendPolling(uid);
        }
      });
    } catch (error: any) {
      console.error("Failed to initialize Firestore listener, using backend polling:", error);
      this.errorLogger.logError(error, 'MediaLibrary: Firestore Listener Init Failed', uid);
      this.startBackendPolling(uid);
    }
  }

  /**
   * Fallback: Poll backend endpoint when Firestore index is not available
   */
  private startBackendPolling(uid: string): void {
    // Clear any existing polling
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }

    // Poll every 5 seconds
    const pollInterval = interval(5000);
    let pollingSub: Subscription | null = null;

    const poll = async () => {
      try {
        const auth = getAuth();
        if (!auth.currentUser) {
          this.itemsSubject.next([]);
          if (pollingSub) pollingSub.unsubscribe();
          return;
        }

        const token = await auth.currentUser.getIdToken().catch(() => null);
        if (!token) return;

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        // Backend extracts uid from auth token, so we only send limit and offset
        const params = new HttpParams()
          .set('limit', '100') // Get more items for polling
          .set('offset', '0');

        this.http.get<any>(this.mediaListUrl, { headers, params })
          .pipe(
            map((raw) => {
              const response = this.unwrapBackendJson(raw) as { items?: any[] };
              return (response.items || [])
                .map((item) => this.mapMediaToMediaItem(item))
                .filter((item): item is MediaItem => !!item)
                .filter((item) => item.uid === uid);
            }),
            catchError(err => {
              this.errorLogger.logError(err, 'MediaLibrary: Backend Polling Error', uid);
              return of([]);
            })
          )
          .subscribe({
            next: (items) => {
              this.itemsSubject.next(items);
            }
          });
      } catch (err) {
        this.errorLogger.logError(err, 'MediaLibrary: Backend Polling Exception', uid);
      }
    };

    // Initial load
    poll();

    // Set up polling subscription
    pollingSub = pollInterval.subscribe(() => {
      poll();
    });

    // Store subscription for cleanup
    this.firestoreUnsubscribe = () => {
      if (pollingSub) {
        pollingSub.unsubscribe();
        pollingSub = null;
      }
    };
  }

  // ---------------------------------------------------------------------------
  // Upload with Progress
  // ---------------------------------------------------------------------------

  uploadMediaWithProgress(
    file: File,
    metadata?: { title?: string; description?: string; category?: MediaCategory; }
  ): Observable<UploadProgress> {

    // Workflow:
    // Preferred: signed upload (fast, supports large files)
    //   1) createMediaUploadUrl -> signed PUT URL + jobId
    //   2) PUT bytes to signed URL (track progress)
    //   3) analyzeMedia(jobId)
    // Fallback: base64 upload
    //   1) uploadMediaToGcs (base64)
    //   2) analyzeMedia(jobId)
    //
    // Final library is stored in Firestore collection `media` (mirrored from jobs).

    return new Observable<UploadProgress>(observer => {
      this.getCurrentUserInfo().then(async ({ uid, sessionId }) => {
        const auth = getAuth();
        if (!auth.currentUser) {
          observer.error({
            status: 'error' as const,
            percentage: 0,
            error: 'Authentication required. Please log in to upload media.'
          });
          return;
        }

        const token = await auth.currentUser.getIdToken().catch(() => null);
        if (!token) {
          observer.error({
            status: 'error' as const,
            percentage: 0,
            error: 'Failed to get authentication token. Please try logging in again.'
          });
          return;
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        const filename = file.name;
        const contentType = file.type || 'application/octet-stream';

        // Backend extracts uid from auth token, so we only send sessionId, filename, and contentType
        const createBody = { sessionId, filename, contentType };

        let uploadInfo: CreateMediaUploadUrlResponse | null = null;
        try {
          const rawCreate = await firstValueFrom(
            this.http.post<any>(this.createUploadUrlEndpoint, createBody, { headers })
          );
          uploadInfo = this.unwrapBackendJson(rawCreate) as CreateMediaUploadUrlResponse;
        } catch (err: any) {
          // If signed URL creation fails (common when IAM signBlob not permitted), fallback to backend upload.
          this.errorLogger.logError(err, 'MediaLibrary: createMediaUploadUrl failed', uid);
          try {
            observer.next({ status: 'progress', percentage: 5 });
            const b64 = await this.fileToBase64(file);
            const rawRes = await firstValueFrom(
              this.http.post<any>(
                    this.uploadMediaToGcsEndpoint,
                { sessionId, filename, contentType, mediaBase64: b64 },
                { headers }
              )
            );
            const jobId = this.extractJobIdFromResponse(rawRes);
            if (!jobId) throw new Error('Upload succeeded but no jobId returned');

            await firstValueFrom(
              this.http
                .post<AnalyzeMediaResponse>(this.analyzeMediaEndpoint, { jobId, sessionId }, { headers })
                .pipe(catchError((e) => {
                  this.errorLogger.logError(e, 'MediaLibrary: analyzeMedia failed (non-fatal)', uid);
                  return of(null);
                }))
            );

            observer.next({ status: 'complete', percentage: 100, mediaId: jobId });
            observer.complete();
          } catch (fallbackErr: any) {
            this.errorLogger.logError(fallbackErr, 'MediaLibrary: uploadMediaToGcs fallback failed', uid);
            observer.error({ status: 'error', percentage: 0, error: fallbackErr?.message || 'Upload failed' });
          }
          return;
        }

        // Try signed URL upload first. If it fails (including CORS-preflight failures which show as status=0), fallback to base64.
        const putHeaders = new HttpHeaders({ 'Content-Type': uploadInfo.contentType || contentType });

        const putSub = this.http.put(uploadInfo.uploadUrl, file, {
          headers: putHeaders,
          reportProgress: true,
          observe: 'events'
        }).pipe(
          catchError(async (err) => {
            // Fallback path: uploadMediaToGcs (base64)
            const asAny: any = err;
            const status = asAny?.status;
            // status=0 is common for browser-blocked CORS failures.
            if (status === 0 || status === 403 || status === 500) {
              try {
                observer.next({ status: 'progress', percentage: 5 });
                const b64 = await this.fileToBase64(file);
                const rawRes = await firstValueFrom(
                  this.http.post<any>(
                    this.uploadMediaToGcsEndpoint,
                    {
                      sessionId,
                      filename,
                      contentType,
                      mediaBase64: b64
                    },
                    { headers }
                  )
                );
                const jobId = this.extractJobIdFromResponse(rawRes);
                if (!jobId) throw new Error('Upload succeeded but no jobId returned');

                await firstValueFrom(
                  this.http
                    .post<AnalyzeMediaResponse>(this.analyzeMediaEndpoint, { jobId, sessionId }, { headers })
                    .pipe(catchError((e) => {
                      this.errorLogger.logError(e, 'MediaLibrary: analyzeMedia failed (non-fatal)', uid);
                      return of(null);
                    }))
                );

                observer.next({ status: 'complete', percentage: 100, mediaId: jobId });
                observer.complete();
                return of(null);
              } catch (fallbackErr: any) {
                this.errorLogger.logError(fallbackErr, 'MediaLibrary: uploadMediaToGcs fallback failed', uid);
                observer.error({ status: 'error', percentage: 0, error: fallbackErr?.message || 'Upload failed' });
                return of(null);
              }
            }

            this.errorLogger.logError(err, 'MediaLibrary: Signed PUT upload error', uid);
            observer.error({ status: 'error', percentage: 0, error: asAny?.message || 'Upload failed' });
            return of(null);
          })
        ).subscribe({
          next: (event: any) => {
            if (!event) return;
            if (event.type === HttpEventType.UploadProgress && event.total) {
              const percentage = Math.round((100 * event.loaded) / event.total);
              observer.next({ status: 'progress', percentage });
              return;
            }

            if (event.type === HttpEventType.Response) {
              const jid = this.extractJobIdFromResponse(uploadInfo) || uploadInfo?.jobId;
              if (!jid) {
                observer.error({
                  status: 'error' as const,
                  percentage: 0,
                  error: 'Upload finished but no jobId was returned from the server.'
                });
                return;
              }
              this.http
                .post<AnalyzeMediaResponse>(
                  this.analyzeMediaEndpoint,
                  { jobId: jid, sessionId },
                  { headers }
                )
                .pipe(
                  catchError((err) => {
                    this.errorLogger.logError(err, 'MediaLibrary: analyzeMedia failed (non-fatal)', uid);
                    return of(null as any);
                  })
                )
                .subscribe(() => {
                  observer.next({ status: 'complete', percentage: 100, mediaId: jid });
                observer.complete();
                });
            }
          },
          error: () => {
            // handled in catchError
          }
        });

        return () => putSub.unsubscribe();
      });
    });
  }

  uploadMediaToGcsWithProgress(
    file: File,
    metadata?: { title?: string; description?: string; category?: MediaCategory; }
  ): Observable<UploadProgress> {
    return new Observable<UploadProgress>(observer => {
      this.getCurrentUserInfo().then(async ({ uid, sessionId }) => {
        const auth = getAuth();
        if (!auth.currentUser?.uid) {
          observer.error({
            status: 'error',
            percentage: 0,
            error: 'Authentication required. Please log in to save media.'
          });
          return;
        }

        const token = await auth.currentUser.getIdToken().catch(() => null);
        if (!token) {
          observer.error({
            status: 'error',
            percentage: 0,
            error: 'Failed to get authentication token. Please log in again.'
          });
          return;
        }
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        const filename = file.name;
        const contentType = file.type || 'application/octet-stream';

        try {
          observer.next({ status: 'progress', percentage: 5 });
          const b64 = await this.fileToBase64(file);
          const rawRes = await firstValueFrom(
            this.http.post<any>(
              this.uploadMediaToGcsEndpoint,
              {
                sessionId,
                filename,
                contentType,
                mediaBase64: b64,
                metadata
              },
              { headers }
            )
          );

          const jobId = this.extractJobIdFromResponse(rawRes);
          if (!jobId) {
            const preview = typeof rawRes === 'object' ? JSON.stringify(rawRes).slice(0, 500) : String(rawRes);
            this.errorLogger.logError(
              new Error(`No jobId in upload response: ${preview}`),
              'MediaLibrary: upload response shape',
              uid || 'unknown'
            );
            throw new Error('Upload succeeded but no jobId returned. Check network response shape.');
          }

          await firstValueFrom(
            this.http
              .post<AnalyzeMediaResponse>(this.analyzeMediaEndpoint, { jobId, sessionId }, { headers })
              .pipe(
                catchError((analyzeErr) => {
                  this.errorLogger.logError(
                    analyzeErr,
                    'MediaLibrary: analyzeMedia failed (non-fatal; media is saved)',
                    uid || 'unknown'
                  );
                  return of(null);
                })
              )
          );

          observer.next({ status: 'complete', percentage: 100, mediaId: jobId });
          observer.complete();
        } catch (err: any) {
          this.errorLogger.logError(err, 'MediaLibrary: uploadMediaToGcs failed', uid || 'unknown');
          observer.error({
            status: 'error',
            percentage: 0,
            error: err?.message || 'Upload failed'
          });
        }
      });
    });
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = () => {
        const res = String(reader.result || '');
        // Strip data URL prefix
        const comma = res.indexOf(',');
        resolve(comma >= 0 ? res.slice(comma + 1) : res);
      };
      reader.readAsDataURL(file);
    });
  }

  // ---------------------------------------------------------------------------
  // Analysis polling (Firestore status)
  // ---------------------------------------------------------------------------

  /**
   * Preferred (no polling): listen to Firestore docs in mediaJobs/{jobId}.
   *
   * This provides realtime progress updates for Video Intelligence jobs
   * because the backend writes `progressPercent` + `status` into the job doc.
   */
  watchAnalysisForJobIds(jobIds: string[]): Observable<AnalysisProgressState> {
    const ids = (jobIds || []).filter(Boolean);
    const total = ids.length;

    if (!total) {
      return of({ state: 'failed', progress: 100, done: 0, total: 0, ready: 0, failed: 0 });
    }

    return new Observable<AnalysisProgressState>((observer) => {
      const stateById = new Map<string, MediaJobDoc>();
      const unsubs: Unsubscribe[] = [];

      const emit = () => {
        let ready = 0;
        let failed = 0;
        let accumProgress = 0;

        ids.forEach((id) => {
          const d = stateById.get(id);
          // We watch the final `media` doc (not mediaJobs), so status is UI-friendly:
          // - ready | processing | failed
          // but we also support job-like status if present.
          const statusRaw = String((d as any)?.status || '').toUpperCase();
          const jobStatusRaw = String((d as any)?.jobStatus || '').toUpperCase();
          const st = (jobStatusRaw || statusRaw || 'UPLOADING').toUpperCase();
          const pct = typeof d?.progressPercent === 'number' ? d.progressPercent : null;

          if (st === 'COMPLETED') ready++;
          else if (st === 'ERROR') failed++;
          else {
            const uiSt = String((d as any)?.status || '').toLowerCase();
            if (uiSt === 'ready') ready++;
            else if (uiSt === 'failed') failed++;
          }

          if (pct != null) accumProgress += Math.max(0, Math.min(100, pct));
          else {
            // fallback heuristics when progressPercent isn't present yet
            if (st === 'COMPLETED') accumProgress += 100;
            else if (st === 'ERROR') accumProgress += 100;
            else if (st === 'VIDEO_PROCESSING') accumProgress += 35;
            else if (st === 'ANALYZING') accumProgress += 50;
            else accumProgress += 10;
          }
        });

        const done = ready + failed;
        const progress = Math.min(100, Math.round(accumProgress / total));
        const state: AnalysisState = done === total ? (failed > 0 ? 'failed' : 'ready') : 'processing';

        observer.next({ state, progress, done, total, ready, failed });
        if (state !== 'processing') observer.complete();
      };

        ids.forEach((id) => {
          // Watch the final library doc (avoids Firestore rules on mediaJobs).
          const ref = doc(this.db, this.mediaCollectionName, id);
          const unsub = onSnapshot(
            ref,
            (snap) => {
              stateById.set(id, (snap.data() as any) || { jobId: id } as any);
            emit();
          },
          (err) => observer.error(err)
        );
        unsubs.push(unsub);
      });

      // Initial emit
      emit();

      return () => {
        unsubs.forEach((u) => u());
      };
    }).pipe(takeWhile((s) => s.state === 'processing', true));
  }

  /**
   * Redundancy polling: calls backend pollMediaJob for each jobId.
   * Useful if Firestore listeners are unavailable.
   */
  pollAnalysisForJobIds(jobIds: string[], pollMs: number = 2000): Observable<AnalysisProgressState> {
    const ids = (jobIds || []).filter(Boolean);
    const total = ids.length;

    if (!total) {
      return of({ state: 'failed', progress: 100, done: 0, total: 0, ready: 0, failed: 0 });
    }

    return interval(pollMs).pipe(
      startWith(0),
      switchMap(() => from(this.getRequiredAuthHeaders())),
      switchMap((headers) => {
        const calls = ids.map((jobId) =>
          this.http.post<any>(this.pollMediaJobEndpoint, { jobId }, { headers }).pipe(
            map((raw) => this.unwrapBackendJson(raw) as MediaJobDoc),
            catchError(() => of({ jobId, status: 'ERROR' } as any))
          )
        );
        return forkJoin(calls);
      }),
      map((jobs) => {
        let ready = 0;
        let failed = 0;
        let accumProgress = 0;
        jobs.forEach((j) => {
          const st = String(j?.status || '').toUpperCase();
          const pct = typeof j?.progressPercent === 'number' ? j.progressPercent : null;
          if (st === 'COMPLETED') ready++;
          else if (st === 'ERROR') failed++;
          accumProgress += pct != null ? Math.max(0, Math.min(100, pct)) : (st === 'COMPLETED' || st === 'ERROR' ? 100 : 30);
        });
        const done = ready + failed;
        const progress = Math.min(100, Math.round(accumProgress / total));
        const state: AnalysisState = done === total ? (failed > 0 ? 'failed' : 'ready') : 'processing';
        return { state, progress, done, total, ready, failed } as AnalysisProgressState;
      }),
      takeWhile((s) => s.state === 'processing', true)
    );
  }

  stopAllAnalysisPolling(): void {
    this.analysisPollingSubs.forEach(s => s.unsubscribe());
    this.analysisPollingSubs = [];
  }

  // ---------------------------------------------------------------------------
  // Media Management
  // ---------------------------------------------------------------------------

  async updateMediaItem(id: string, updates: Partial<MediaItem>): Promise<void> {
    try {
      const docRef = doc(this.db, 'media', id);
      await updateDoc(docRef, { ...updates, updatedAt: new Date().toISOString() });
    } catch (error) {
      this.errorLogger.logError(error, 'MediaLibrary: Update Media Item', id);
      throw error;
    }
  }

  async deleteMediaItem(id: string): Promise<void> {
    try {
      const docRef = doc(this.db, 'media', id);
      await deleteDoc(docRef);
    } catch (error) {
      this.errorLogger.logError(error, 'MediaLibrary: Delete Media Item', id);
      throw error;
    }
  }

  async togglePinned(id: string, pinned: boolean): Promise<void> {
    await this.updateMediaItem(id, { pinned });
  }

  async toggleArchived(id: string, archived: boolean): Promise<void> {
    await this.updateMediaItem(id, { archived });
  }

  async changeColor(id: string, color: string): Promise<void> {
    await this.updateMediaItem(id, { color });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private mapMediaToMediaItem(data: any): MediaItem | null {
    if (!data) return null;
    if (String(data.status || '').toLowerCase() === 'deleted') return null;

    const analysis = this.normalizeAnalysisFields(data);
    const bucket = String(data.bucket || '');
    const obj = String(data.object || '');
    const fileName = String(data.fileName || (obj ? (obj.split('/').pop() || '') : ''));
    const mediaUrl = String(data.mediaUrl || (bucket && obj ? this.buildPublicGcsUrl(bucket, obj) : ''));
    const derivedType = this.detectMediaType(data.mimeType || data.contentType);
    const sourceType = String(data.type || '').toLowerCase();
    const normalizedMediaType: MediaType =
      (data.mediaType as MediaType) ||
      (sourceType === 'dalle_image' ? 'image' : sourceType === 'video' ? 'video' : sourceType === 'image' ? 'image' : derivedType);
    const rawStatus = String(data.status || data.jobStatus || '').toLowerCase();
    const normalizedStatus =
      rawStatus === 'active' || rawStatus === 'ready' || rawStatus === 'completed'
        ? 'ready'
        : rawStatus === 'failed' || rawStatus === 'error'
          ? 'failed'
          : 'processing';

    const id = String(data.id || data.mediaId || data.jobId || data.assetId || '').trim();
    const uid = String(data.uid || '').trim();
    if (!id || !uid) return null;

    return {
      id,
      uid,
      sessionId: data.sessionId || 'user-session',
      title: data.title || fileName || '',
      description: analysis.description || '',
      caption: analysis.caption || '',
      labels: data.labels || analysis.categories || data.categories || [],
      // Backend category is free-form; cast to MediaCategory for UI compatibility.
      category: ((analysis.category || data.category) as MediaCategory) || undefined,
      color: data.color || '#ffffff',
      pinned: data.pinned || false,
      archived: data.archived || false,
      mediaType: normalizedMediaType,
      fileName,
      fileSize: data.fileSize,
      mimeType: data.mimeType || data.contentType || '',
      mediaUrl,
      thumbnailUrl: data.thumbnailUrl || mediaUrl || '',
      status: normalizedStatus,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || data.createdAt || new Date().toISOString(),
      orderIndex: data.orderIndex || 0
    };
  }

  private detectMediaType(mimeType: string): MediaType {
    if (!mimeType) return 'unknown';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'unknown';
  }

  private buildPublicGcsUrl(bucket: string, objectName: string): string {
    // Works only if bucket/object is publicly readable.
    // If your bucket is private, add a signed download URL endpoint.
    const enc = encodeURIComponent(objectName).replace(/%2F/g, '/');
    return `https://storage.googleapis.com/${bucket}/${enc}`;
  }

  private async getRequiredAuthHeaders(): Promise<HttpHeaders> {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('Authentication required.');
    }
    const token = await auth.currentUser.getIdToken().catch(() => null);
    if (!token) {
      throw new Error('Failed to get authentication token.');
    }
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  /**
   * Get current user info - requires Firebase Auth
   * Anonymous users are not supported for backend endpoints
   */
  private async getCurrentUserInfo(): Promise<{ uid: string, sessionId: string }> {
    const auth = getAuth();
    const user = auth.currentUser;

    // Do not throw here; upload path already enforces auth.
    const uid = user?.uid || '';
    return { uid, sessionId: this.mediaSessionId };
  }
}